import {
  ref,
  reactive,
  computed,
  onBeforeMount,
  getCurrentInstance,
  watch,
} from "@vue/composition-api";
import store from "@/store";
import useModal from "@/@core/components/base-modal/hooks/useModal";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import useFlatPickr from "@/utils/hooks/useFlatPickr";
import usePagination from "@/views/components/pagination/usePagination";
import { privilegeImageModel } from "@/models/privilege/privilege-form";
import router from "@/router";

export default function usePrivilegeImage(props) {
  const {
    createForm,
    editForm,
    fetchDetail,
    updateMasterImage,
    reorderGroupImageList,
    deleteItemList,
  } = privilegeImageModel();
  const { openModal, closeModal } = useModal();
  const {
    openErrorModal,
    openSuccessModal,
    openWarningModal,
    openDefaultErrorModal,
  } = useValidationModal();
  const { pagination, onPageChange, onFilterEntity } = usePagination();
  const {
    resolveDateString,
    resolveDate,
    resolveTime,
    resolveDateObject,
  } = useFlatPickr();

  const vm = getCurrentInstance().proxy;

  const loading = reactive({
    tableData: false,
    tableOverlay: false,
  });

  const currentIndex = ref(0);
  const currentIdSelect = ref(null);
  const keyChange = ref(0);
  const tableModeReorder = ref(false);

  const isEditImage = ref(false);

  const ImageListTable = reactive({
    payload: [],
  });

  const modalType = reactive({
    reorder: false,
    manageImageList: false,
  });

  const isEditData = ref(false);

  const formData = reactive({
    privilegeId: 0,
    imageMaster: {
      file: null,
      url: null,
    },
  });

  const imageDetailItem = reactive({
    privilegeId: 0,
    selectId: 0,
  });

  const fields = [
    { key: "seq", label: "No." },
    { key: "image_url", label: "Image" },
    { key: "type", label: "Image Type" },
    { key: "name", label: "Name" },
    { key: "key", label: "Key" },
    { key: "latest_update", label: "Latest Update" },
    { key: "actions", label: "" },
  ];

  const handleRowChange = (event) => {
    fetchReorderList(
      ImageListTable.payload[event.oldDraggableIndex].group_image_id,
      ImageListTable.payload[event.oldDraggableIndex].seq,
      ImageListTable.payload[event.newDraggableIndex].seq
    );
    ImageListTable.payload.splice(
      event.newDraggableIndex,
      0,
      ImageListTable.payload.splice(event.oldDraggableIndex, 1)[0]
    );
    keyChange.value++;
  };

  const sortableRowOptions = reactive({
    chosenClass: "is-selected",
    ghostClass: "sortable-ghost",
    onEnd: handleRowChange,
    disabled: true,
    animation: 300,
    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
  });

  const fetchReorderList = (id, currentPosition, newPosition) => {
    loading.tableOverlay = true;
    reorderGroupImageList.payload.Group_Image_Id = id;
    reorderGroupImageList.payload.Privilege_Id = formData.privilegeId;
    reorderGroupImageList.payload.From = currentPosition;
    reorderGroupImageList.payload.To = newPosition;
    store
      .dispatch("api/apiRequest", reorderGroupImageList)
      .then((response) => {
        fetchImageList(false);
      })
      .catch((error) => {
        openDefaultErrorModal(error);
        loading.tableOverlay = false;
      });
  };

  const onClickReorder = (mode) => {
    mappingActionList.reorder.display = !mappingActionList.reorder.display;
    tableModeReorder.value = !tableModeReorder.value;
    sortableRowOptions.disabled = !tableModeReorder.value;
  };

  const mappingActionList = reactive({
    edit: {
      label: "Edit",
      value: 1,
      icon: "EditIcon",
      display: true,
    },
    reorder: {
      label: "Reorder",
      value: 2,
      icon: "ReorderIcon",
      display: false,
    },
    delete: {
      label: "Delete",
      value: 3,
      icon: "DeleteIcon",
      display: true,
    },
  });

  const resolveMappingTableAction = (type, item) => {
    switch (type) {
      case 1:
        openManageImageListModal(item.item.group_image_id, true);
        break;
      case 2:
        openReorderModal(item.item);
        break;
      case 3:
        openDeleteWarningModal(item.item);
        break;
      default:
    }
  };

  const openDeleteWarningModal = (itemData) => {
    openWarningModal("ลบ Image", `ต้องการลบ ${itemData.name} หรือไม่`, () => {
      deleteImageList(itemData.group_image_id);
    });
  };

  const deleteImageList = (id) => {
    window.$loading(true);
    deleteItemList.payload.Privilege_Id = formData.privilegeId; // required
    deleteItemList.payload.Group_Image_Id = id; // required
    deleteItemList.payload.Req_IdentityId = 1; // required

    store
      .dispatch("api/apiRequest", deleteItemList)
      .then((response) => {
        openSuccessModal(
          "ทำรายการสำเร็จ !",
          null,
          "ตกลง",
          () => fetchImageList(false),
          () => fetchImageList(false)
        );
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const openReorderModal = (item) => {
    modalType.reorder = true;
    currentIndex.value = item.seq;
    currentIdSelect.value = item.group_image_id;
    openModal();
  };

  const reorderImageList = async (current, newIndex) => {
    fetchReorderList(currentIdSelect.value, currentIndex.value, newIndex);

    keyChange.value++;
    closeModal();
  };

  const closeReorderModal = () => {
    modalType.reorder = false;
    closeModal();
  };

  const openManageImageListModal = (selectId, isEdit) => {
    isEditImage.value = false;
    if (selectId) imageDetailItem.selectId = selectId;
    if (isEdit && isEdit == true) {
      isEditImage.value = true;
    }

    imageDetailItem.privilegeId = formData.privilegeId;

    modalType.manageImageList = true;
    openModal();
  };

  const closeManageImageListModal = () => {
    modalType.manageImageList = false;
    closeModal();
  };

  const cleanObj = (objData) => {
    objData.forEach((obj) => {
      for (const propName in obj) {
        if (obj[propName] === null || obj[propName] === undefined) {
          delete obj[propName];
        }
      }
      return obj;
    });
    return objData;
  };

  const saveImageList = (inputData, isEditModal = false) => {
    window.$loading(true);
    const payload = {
      Privilege_Id: formData.privilegeId, // required
      Req_IdentityId: 1, // required
      Group_Image: [
        {
          Type: inputData.imageType, // required
          Name: inputData.name, // required
          Key: inputData.key ? inputData.key : null,
          Remark: inputData.remark ? inputData.remark : null,
          Desc_Th: inputData.descTh ? inputData.descTh : null,
          Desc_En: inputData.descEn ? inputData.descEn : null,
          ImageList:
            inputData.imageList.length > 0
              ? inputData.imageList
                  .filter((o) => o.image_id == 0)
                  .map((obj, index) => ({
                    Image: obj.file,
                    Seq:
                      inputData.imageType !== 1
                        ? inputData.imageList.filter((o) => o.image_id !== 0)
                            .length +
                          index +
                          1
                        : 1,
                  }))
              : [],
        },
      ],
    };

    createForm.payload = payload;
    editForm.payload = payload;

    let payloadParam = {};

    if (isEditModal) {
      editForm.payload.Group_Image = cleanObj(editForm.payload.Group_Image);
      editForm.payload.Group_Image[0].Group_Image_Id = inputData.groupImageId;
      editForm.payload.Group_Image[0].ImageList_Delete =
        inputData.imageDelList.length > 0 ? inputData.imageDelList : [];

      payloadParam = editForm;
    } else {
      createForm.payload.Group_Image = cleanObj(createForm.payload.Group_Image);

      payloadParam = createForm;
    }

    store
      .dispatch("api/apiFormRequest", payloadParam)
      .then(async (resp) => {
        modalType.manageImageList = false;
        await fetchImageList();
        await closeModal();
      })
      .catch((err) => {
        openDefaultErrorModal();
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const onUploadImage = (fileData) => {
    formData.imageMaster.file = fileData.file;
    formData.imageMaster.url = fileData.url;
  };

  const onDeleteImage = (fileData) => {
    formData.imageMaster.file = fileData.file;
    formData.imageMaster.url = fileData.url;
  };

  const fetchImageDetail = () => {
    window.$loading(true);

    const payload = {
      Privilege_Id: formData.privilegeId,
      Page: pagination.currentPage,
      Limit: pagination.perPage,
    };

    fetchDetail.payload = payload;

    store
      .dispatch("api/apiRequest", fetchDetail)
      .then(async (resp) => {
        const respData = resp.data.resp;
        ImageListTable.payload = respData.privilege_image.privilege_group_image;
        formData.imageMaster.url = respData.privilege_image.privilege_image_url;
        pagination.rows = respData.privilege_image.privilege_group_image.length;
        pagination.totalRows = respData.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal();
      })
      .finally(() => {
        window.$loading(false);
        isEditData.value = false;
      });
  };

  const fetchImageList = (showLoading = true) => {
    if (showLoading) {
      loading.tableData = true;
    }
    const payload = {
      Privilege_Id: formData.privilegeId,
      Page: pagination.currentPage,
      Limit: pagination.perPage,
    };

    fetchDetail.payload = payload;

    store
      .dispatch("api/apiRequest", fetchDetail)
      .then(async (resp) => {
        const respData = resp.data.resp;
        ImageListTable.payload = respData.privilege_image.privilege_group_image;
        pagination.rows = respData.privilege_image.privilege_group_image.length;
        pagination.totalRows = respData.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal();
      })
      .finally(() => {
        loading.tableData = false;
        loading.tableOverlay = false;
      });
  };

  const manageImageForm = (isExit = false) => {
    window.$loading(true);
    const payload = {
      Privilege_Id: formData.privilegeId,
      Req_IdentityId: 1,
      Main_Image: formData.imageMaster.file,
    };

    updateMasterImage.payload = payload;

    store
      .dispatch("api/apiFormRequest", updateMasterImage)
      .then(async (resp) => {
        const respData = resp.data.resp;
        ImageListTable.payload = respData.privilege_group_image;
        formData.imageMaster.url = respData.privilege_image_url;
        vm.$emit("onGetPrivilegeId", formData.privilegeId);
        await fetchImageDetail();
        if (isExit) return vm.$emit("onPreviousStep");
        await nextStep();
      })
      .catch((err) => {
        openDefaultErrorModal();
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const openWarningSaveData = () => {
    openWarningModal(
      "ต้องการบันทึกการเปลี่ยนแปลงหรือไม่",
      null,
      () => {
        manageImageForm(true);
      },
      () => {
        vm.$emit("onPreviousStep");
      }
    );
  };

  const onSubmitForm = () => {
    if (isEditData.value == true) {
      manageImageForm();
    } else {
      nextStep();
    }
  };

  const onBack = async () => {
    if (isEditData.value == true) {
      await openWarningSaveData();
    } else {
      vm.$emit("onPreviousStep");
    }
  };

  const nextStep = () => {
    window.scrollToTop();
    vm.$emit("onNextStep");
  };

  const checkFormCreateOrEdit = () => {
    if (router.currentRoute.query.privilegeId) {
      vm.$emit(
        "onGetPrivilegeId",
        parseInt(router.currentRoute.query.privilegeId)
      );
    }
  };

  onBeforeMount(async () => {
    await checkFormCreateOrEdit();
    if (props.privilegeId || props.isEdit) {
      formData.privilegeId = props.privilegeId;
      await fetchImageDetail();
    }
  });

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchImageList();
      }
    },
    { deep: true }
  );

  watch(
    formData,
    () => {
      isEditData.value = true;
    },
    { deep: true }
  );

  return {
    loading,
    pagination,
    onPageChange,
    onFilterEntity,
    formData,
    currentIndex,
    fields,
    ImageListTable,
    sortableRowOptions,
    keyChange,
    mappingActionList,
    tableModeReorder,
    isEditImage,
    modalType,
    reorderImageList,
    resolveMappingTableAction,
    onUploadImage,
    onDeleteImage,
    onClickReorder,
    closeModal,
    openManageImageListModal,
    closeManageImageListModal,
    closeReorderModal,
    saveImageList,
    onSubmitForm,
    resolveDate,
    resolveTime,
    resolveDateString,
    resolveDateObject,
    imageDetailItem,
    onBack,
  };
}
